// ── THE KANBAN BOARD'S COLUMNS — THE ONE LIST ─────────────────────────────
//
// Every place an invoice's board `status` is shown or changed reads this list:
// the board itself and its card modal (Dashboard), the Board dropdown on
// ViewDocument, and the Board Status dropdowns in NewDocument and EditDocument.
// Those dropdowns used to be hand-written copies. They drifted: they were in a
// different order from the board, and none of them offered Not Ordered /
// Pending or Special Order Items, so a card could not be moved there from them.
//
// ORDER HERE IS THE BOARD'S ORDER, left to right, and every dropdown shows the
// columns in this order.
//
// `id` IS THE VALUE STORED ON THE INVOICE, in its `status` field. `label` is
// only what the column heading and the dropdowns say. They are allowed to
// differ and in one case deliberately do:
//
//   "Hoco/Fallston Jobs" was RENAMED TO "Install Jobs" — the LABEL only. Every
//   invoice already sitting on that column carries the string
//   "Hoco/Fallston Jobs" in Firestore. Changing the id would leave all of them
//   pointing at a status no column claims, and the board's loader sweeps an
//   unrecognised status into Ordered — so renaming the key would silently empty
//   the column and dump its cards into a different one. The wording is free to
//   change; the stored vocabulary is not.
//
// ADDING a column is safe in a way renaming one is not: nothing carries the new
// value yet, so the new column simply starts empty and no existing card moves.
// Colours are the board's business and live in Dashboard.js, keyed by id.
export const BOARD_COLUMNS = [
  { id: 'Not Ordered',         label: '⏳ Not Ordered / Pending' },
  { id: 'Ordered',             label: '📝 Ordered / Placed' },
  { id: 'Special Order Items', label: '🛒 Special Order Items' },
  { id: 'Deliveries',          label: '🚚 Deliveries' },
  { id: 'Back Order',          label: '⏸️ Back Order' },
  { id: 'Picked Up',           label: '✅ Picked Up / Delivered' },
  // Stored value unchanged — see the note above.
  { id: 'Hoco/Fallston Jobs',  label: '🏗️ Install Jobs' },
];

// Where a new invoice (created, converted from a quote, or cloned) starts, and
// what a missing or unrecognised status is read as.
export const DEFAULT_BOARD_STATUS = 'Ordered';

// The INSTALL JOBS column, by its STORED key — the label reads "Install Jobs",
// the stored value is still 'Hoco/Fallston Jobs' (see the note above). Named
// here so screens that treat installation work differently from counter sales
// (Reports) can ask for it without writing the string out again, which would
// then be missed if the key ever did change.
export const INSTALL_JOBS_STATUS = 'Hoco/Fallston Jobs';

/**
 * Is this document CURRENTLY in the Install Jobs column?
 *
 * Current status only: a document moved out of that column is an ordinary sale
 * again, and one moved in stops being one — there is no record of where it has
 * been, and the board is what the office keeps up to date.
 *
 * A document with NO status — every quote, since the board carries invoices
 * only, and older invoices from before the board existed — is NOT an install
 * job. It was never marked as one.
 */
export const isInstallJob = (doc) => doc?.status === INSTALL_JOBS_STATUS;

// For <select>s: { value, label } in board order.
export const BOARD_STATUS_OPTIONS = BOARD_COLUMNS.map(c => ({ value: c.id, label: c.label }));
